import React, { Component } from "react";
import { observer } from "mobx-react";
import LinkedinIcon from "./Icons/LinkedinIcon";
import FacebookIcon from "./Icons/FacebookIcon";
import GoogleIcon from "./Icons/GoogleIcon";
import Forms from "./Forms";
import TermsAndConditions from "../../../components/TermsAndConditions";
import ModalStore from "../../../stores/ModalStore";
import LoginStore from "../../../stores/LoginStore";

interface SignInFormContainerProps {
  redirectUrl: string;
  loginUser: (e: React.MouseEvent<HTMLElement>) => void;
  modalStore: ModalStore;
  loginStore: LoginStore;
}

interface SignInFormContainerState {
  greeting: string;
}

interface GreetingPools {
  byDate: { [key: string]: string[] };
  byTime: { [key: string]: string[] };
  byDay: { [key: string]: string[] };
  byLocale: { [key: string]: string[] };
  general: string[];
}

const greetingPools: GreetingPools = {
  byDate: {
    christmaseve: [
      "Christmas Eve, eh?",
      "Last-minute scholar?",
      "Twas the night before...",
    ],
    christmas: [
      "Happy Christmas!",
      "Mince pie scholar",
      "Christmas brain",
      "Sneaking some learning?",
    ],
    boxingday: [
      "Boxing Day brain",
      "Recovering, are we?",
      "Leftovers and learning",
    ],
    newyearseve: [
      "Last study of the year",
      "On the cusp",
      "Year-end curiosity",
    ],
    newyearsday: [
      "Happy New Year!",
      "Fresh year, fresh you",
      "Resolution mode?",
      "Year one of you",
    ],
    valentines: [
      "Be your own valentine",
      "Heart for learning",
      "Self-love, mind food",
    ],
    iwd: ["Happy IWD!", "International Women's Day", "Today is hers"],
    halloween: [
      "Spooky studious",
      "Trick or learn",
      "Halloween homework?",
      "Costume off, books on",
    ],
  },
  byTime: {
    earlymorning: [
      "Up early, are you a baker?",
      "Goodness, you're keen",
      "Up with the magpies",
      "First in, eh?",
      "Dawn brain",
    ],
    morning: [
      "Top of the morning",
      "Coffee in hand?",
      "Morning, you",
      "Buenos d\u00edas",
      "Guten Morgen",
      "Bright and early",
    ],
    latemorning: [
      "Pre-lunch curiosity?",
      "Lunch on the horizon",
      "11 o'clock energy",
      "Stomach speaking yet?",
    ],
    lunch: [
      "Bravo on the lunch & learn",
      "Lunchtime learner",
      "Buenas tardes",
      "Eating al desko?",
      "Sandwich scholar",
    ],
    afternoon: [
      "Howdy partner",
      "Three o'clock saviour",
      "Coffee number three?",
      "Afternoon reboot?",
    ],
    lateafternoon: [
      "Knocking-off hours",
      "Evening, you",
      "Beer o'clock approaches",
      "Bonsoir",
    ],
    evening: [
      "Sofa scholar",
      "Pyjama scholar",
      "Telly off?",
      "Buona sera",
      "Dinner digested?",
    ],
    latenight: [
      "Burning the midnight oil?",
      "Hello, night owl",
      "Witching hour",
    ],
    graveyard: [
      "Couldn't sleep?",
      "3am wisdom run?",
      "Insomnia or inspiration?",
      "Different timezone?",
      "The graveyard hour",
    ],
  },
  byDay: {
    mon: [
      "Brave to show up on a Monday",
      "Welcome back to the wheel",
      "Mondaying through it",
    ],
    wed: ["Halfway up the mountain", "Hump day energy"],
    thu: ["Thursday, almost there", "The Friday eve"],
    fri: ["Friday at last", "Almost the weekend", "Friday brain"],
    sat: ["Saturday studious", "Weekend learner", "Saturday brain on"],
    sun: ["Sunday session", "Quiet Sunday brain", "Sunday refuelling"],
  },
  byLocale: {
    es: [
      "Hola",
      "Hola, amigo",
      "Buenas",
      "\u00bfQu\u00e9 tal?",
      "\u00bfQu\u00e9 pasa?",
    ],
    fr: ["Bonjour", "Salut", "Coucou", "Bonjour, mon ami"],
    it: ["Ciao", "Salve", "Ehil\u00e0", "Buongiorno"],
    de: ["Hallo", "Servus", "Moin", "Gr\u00fc\u00df dich"],
    pt: ["Ol\u00e1", "Oi", "E a\u00ed", "Tudo bem?"],
    ja: ["Konnichiwa", "Yaho"],
    ko: ["Annyeong"],
    ru: ["Privet", "Zdorovo"],
    ar: ["Salam", "Marhaba", "Ahlan"],
    hi: ["Namaste", "Namaskar"],
    pl: ["Cze\u015b\u0107", "Hej tam", "Witaj"],
    tr: ["Merhaba", "Selam"],
    sv: ["Hej", "Tjena", "Hejsan"],
    nl: ["Hallo", "Hoi", "H\u00e9"],
    el: ["Yassas", "Yia sou"],
    zh: ["Ni hao", "Hello!"],
  },
  general: [
    "Why hello there",
    "Well well well",
    "Look who it is",
    "Pull up a chair",
    "Tally-ho",
    "Cooee!",
    "Why lookee here",
  ],
};

let lastGreeting: string | null = null;

const getTimeBucket = () => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 7) return "earlymorning";
  if (hour >= 7 && hour < 10) return "morning";
  if (hour >= 10 && hour < 12) return "latemorning";
  if (hour >= 12 && hour < 14) return "lunch";
  if (hour >= 14 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 19) return "lateafternoon";
  if (hour >= 19 && hour < 22) return "evening";
  if (hour >= 22 || hour < 1) return "latenight";

  return "graveyard";
};

const getDayKey = () =>
  ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()];

const getDateKey = () => {
  const today = new Date();
  const key = `${today.getMonth() + 1}-${today.getDate()}`;

  switch (key) {
    case "12-24":
      return "christmaseve";
    case "12-25":
      return "christmas";
    case "12-26":
      return "boxingday";
    case "12-31":
      return "newyearseve";
    case "1-1":
      return "newyearsday";
    case "2-14":
      return "valentines";
    case "3-8":
      return "iwd";
    case "10-31":
      return "halloween";
    default:
      return null;
  }
};

const getLocaleKey = () => {
  if (typeof navigator === "undefined") return null;

  const language = (navigator.language || "en").toLowerCase().split("-")[0];

  return greetingPools.byLocale[language] ? language : null;
};

const pickFromPool = (pool: string[]) => {
  let greeting = pool[Math.floor(Math.random() * pool.length)];

  if (greeting === lastGreeting && pool.length > 1) {
    greeting = pool.filter((option) => option !== lastGreeting)[
      Math.floor(Math.random() * (pool.length - 1))
    ];
  }

  lastGreeting = greeting;

  return greeting;
};

const pickGreeting = () => {
  const dateKey = getDateKey();

  if (dateKey && greetingPools.byDate[dateKey]?.length) {
    return pickFromPool(greetingPools.byDate[dateKey]);
  }

  const timePool = greetingPools.byTime[getTimeBucket()];
  const dayPool = greetingPools.byDay[getDayKey()];
  const localeKey = getLocaleKey();
  const roll = Math.random();

  if (roll < 0.8 && timePool?.length) return pickFromPool(timePool);
  if (roll < 0.9 && dayPool?.length) return pickFromPool(dayPool);
  if (roll < 0.95 && localeKey)
    return pickFromPool(greetingPools.byLocale[localeKey]);

  return pickFromPool(greetingPools.general);
};

class SignInFormContainer extends Component<
  SignInFormContainerProps,
  SignInFormContainerState
> {
  constructor(props) {
    super(props);

    this.state = {
      greeting: pickGreeting(),
    };
  }

  handleShowTermsModal = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.props.modalStore.open();
  };

  render() {
    const { redirectUrl, modalStore, loginStore, loginUser } = this.props;
    const { greeting } = this.state;

    return (
      <div id="sign_in">
        {redirectUrl ? (
          <div id="main_header" className="signin-copy">
            <p
              className="signin-greeting"
              data-greeting={true}
              aria-live="polite"
            >
              Step 1 of 2
            </p>
            <h2 className="signin-headline" id="signin-title">
              Sign in to <em>continue.</em>
            </h2>
          </div>
        ) : (
          <div id="main_header" className="signin-copy">
            <p
              className="signin-greeting"
              data-greeting={true}
              aria-live="polite"
            >
              {greeting}
            </p>
            <h2 className="signin-headline" id="signin-title">
              Sign in or <em>join.</em>
            </h2>
          </div>
        )}
        <div className="signin-panel-content">
          <div
            id="login_mobile_providers"
            className="signin-social"
            role="group"
            aria-label="Sign in or sign up with a social account"
          >
            <a
              className="btn-social click-busy linkedin"
              href="/oauth/linkedin"
              aria-label="Continue with LinkedIn"
            >
              <span className="logo-slot" aria-hidden="true">
                <LinkedinIcon />
              </span>
              <span className="label">LinkedIn</span>
            </a>
            <a
              className="btn-social click-busy google"
              href="/oauth/google"
              aria-label="Continue with Google"
            >
              <span className="logo-slot" aria-hidden="true">
                <GoogleIcon />
              </span>
              <span className="label">Google</span>
            </a>
            <a
              className="btn-social click-busy facebook"
              href="/oauth/facebook"
              aria-label="Continue with Facebook"
            >
              <span className="logo-slot" aria-hidden="true">
                <FacebookIcon />
              </span>
              <span className="label">Facebook</span>
            </a>
          </div>
          <p
            className="signin-divider"
            data-state-block="initial returning new"
          >
            <span>or, old school.</span>
          </p>
          <div className="login-container">
            <Forms loginStore={loginStore} loginUser={loginUser} />
          </div>
          <div className="signin-foot">
            <TermsAndConditions modalStore={modalStore} />
            <p id="login_terms">
              <a
                href="/terms/full"
                onClick={(e) => this.handleShowTermsModal(e)}
              >
                Terms and Conditions
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default observer(SignInFormContainer);
